/**
 * @fileoverview Validation utilities for the transcript modal form.
 *
 * This module provides validation functions for email addresses, share with fields,
 * and note length validation used in the Send Transcript modal form.
 *
 * @author Palash
 * @version 1.0.0
 */

//#region Imports Packages and Libs

import { StringUtils } from "../../../helpers/string_utils";
import { Util } from "../../../util";
import ERRORS from "./error_messages";

//#endregion

//#region Types and Constants

/**
 * Maximum allowed length for note field
 */
const MAX_NOTE_LENGTH = 5000;

/**
 * Special keyword that can be used instead of email addresses
 */
const SUPPORT_KEYWORD = "support";

/**
 * Maximum number of email addresses allowed in share with field
 */
const MAX_SHARE_WITH_EMAILS = 5;

/**
 * Result of parsing the share with field
 */
export interface ShareWithParseResult {
	/** Whether the input is valid */
	valid: boolean;
	/** Array of parsed email addresses (empty if using support keyword) */
	emails: string[];
	/** Error key for i18n resolution (if validation failed) */
	errorKey?: string;
	/** Error message if validation failed (empty if valid) */
	errorMessage?: string;
}

//#endregion

//#region Email Validation

/**
 * Validates if a string is a valid email address format.
 *
 * @param email - The email address to validate
 * @returns True if the email format is valid, false otherwise
 *
 * @example
 * ```typescript
 * isValidEmail("user@example.com") // returns true
 * isValidEmail("invalid-email") // returns false
 * ```
 */
export function isValidEmail(email: string): boolean {
	// Check if email is a string and not empty or whitespace
	if (StringUtils.isNullOrWhitespace(email)) return false;

	// Source: https://v2.vuejs.org/v2/cookbook/form-validation.html
	const emailRegex =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return emailRegex.test(email);
}

//#endregion

//#region Support Keyword Validation

/**
 * Checks if the input is the special "support" keyword.
 * The comparison is case-insensitive and trims whitespace.
 *
 * @param input - The input string to check
 * @returns True if the input is "support" (case-insensitive), false otherwise
 *
 * @example
 * ```typescript
 * isSupportInput("support") // returns true
 * isSupportInput("SUPPORT") // returns true
 * isSupportInput(" Support ") // returns true
 * isSupportInput("other") // returns false
 * ```
 */
export function isSupportInput(input: string): boolean {
	if (!input || typeof input !== "string") {
		return false; // Handle null, undefined, or non-string inputs
	}
	return StringUtils.normalizeString(input) === SUPPORT_KEYWORD.toLowerCase();
}

//#endregion

//#region Share With Field Validation

/**
 * Validates if the input string is empty or whitespace
 *
 * @param input - The input string to validate
 * @returns ShareWithParseResult indicating if input is valid
 */
function validateInputPresence(input: string): ShareWithParseResult | null {
	// Check if input is null, undefined, or empty string
	if (StringUtils.isNullOrWhitespace(input)) {
		// If input is invalid, return error result
		return { valid: false, emails: [], errorMessage: ERRORS.SHARE_WITH_REQUIRED };
	}
	return null; // If input is valid, return null to indicate no error
}

/**
 * Splits input by commas and validates the resulting parts
 *
 * @param parts - Array of email parts after splitting and trimming
 * @returns ShareWithParseResult indicating if parts are valid
 */
function validateParts(parts: string[]): ShareWithParseResult | null {
	if (parts && Array.isArray(parts) && parts.length === 0) {
		// If no parts after splitting, return error
		return { valid: false, emails: [], errorMessage: ERRORS.SHARE_WITH_REQUIRED };
	}
	return null;
}

/**
 * Validates support keyword usage
 *
 * @param parts - Array of email parts after splitting and trimming
 * @returns ShareWithParseResult for support keyword result or null if not using support
 */
function validateSupportKeyword(parts: string[]): ShareWithParseResult | null {
	// Handle single "support" keyword
	if (parts.length === 1 && isSupportInput(parts[0])) {
		return { valid: true, emails: [SUPPORT_KEYWORD] };
	}

	// Check if mixing support keyword with other inputs (not allowed)
	if (parts.some((part) => isSupportInput(part))) {
		return { valid: false, emails: [], errorKey: "error_sharewith_invalid" };
	}

	return null;
}

/**
 * Validates email addresses format
 *
 * @param parts - Array of email parts after splitting and trimming
 * @returns ShareWithParseResult indicating if all emails are valid
 */
function validateEmails(parts: string[]): ShareWithParseResult | null {
	const invalidEmail = parts.some((part) => !isValidEmail(part));
	if (invalidEmail) {
		return { valid: false, emails: [], errorKey: "sharewith_invalid" };
	}
	return null;
}

/**
 * Validates maximum number of email addresses
 *
 * @param parts - Array of email parts after splitting and trimming
 * @returns ShareWithParseResult indicating if number of emails is valid
 */
function validateEmailCount(parts: string[]): ShareWithParseResult | null {
	if (parts.length > MAX_SHARE_WITH_EMAILS) {
		return { valid: false, emails: [], errorKey: "error_sharewith_max_emails" };
	}
	return null;
}

/**
 * Validates email addresses for duplicates
 *
 * @param parts - Array of email parts after splitting and trimming
 * @returns ShareWithParseResult indicating if there are duplicate emails
 */
function validateDuplicateEmails(parts: string[]): ShareWithParseResult | null {
	// Create a normalized version of each email (lowercase) for comparison
	const normalizedEmails = parts.map((email) => StringUtils.normalizeString(email));

	// Find duplicates by checking if any email appears at a position different from its first occurrence
	const duplicateEmail = normalizedEmails.find((email, index) => normalizedEmails.indexOf(email) !== index);
	if (duplicateEmail) {
		return { valid: false, emails: [], errorKey: "error_sharewith_duplicates" };
	}

	return null;
}

/**
 * Parses and validates the share with field input.
 *
 * Accepts either:
 * - A single "support" keyword
 * - A comma-separated list of valid email addresses (up to 5)
 *
 * @param input - The share with field input to parse
 * @returns Parse result with validation status, emails array (if valid), and error message (if invalid)
 */
export function parseShareWith(input: string): ShareWithParseResult {
	try {
		// [1] Check if input is empty or whitespace
		const presenceResult = validateInputPresence(input);
		if (presenceResult) return presenceResult;

		// Split input by commas and trim whitespace
		const parts = input.split(",").map((part) => Util.trimString(part));

		// [2] Validate parts existence
		const partsResult = validateParts(parts);
		if (partsResult) return partsResult;

		// [3] Validate support keyword usage
		const supportResult = validateSupportKeyword(parts);
		if (supportResult) return supportResult;

		// [4] Validate all email addresses
		const emailsResult = validateEmails(parts);
		if (emailsResult) return emailsResult;

		// [5] Validate number of email addresses
		const countResult = validateEmailCount(parts);
		if (countResult) return countResult;

		// [6] Validate duplicate email addresses
		const duplicateResult = validateDuplicateEmails(parts);
		if (duplicateResult) return duplicateResult;

		// All validations passed
		return { valid: true, emails: parts };
	} catch (error) {
		// Log error for debugging but don't expose internal details to user
		console.error("[parseShareWith] Unexpected error during validation:", error);

		// Return safe fallback result
		return { valid: false, emails: [], errorKey: "error_sharewith_invalid" };
	}
}

//#endregion

//#region Note Validation

/**
 * Validates the length of a note. Returns true if the note is empty (optional)
 * or does not exceed the maximum allowed length.
 *
 * @param {string} note - The note text to validate.
 * @returns {boolean} - True if valid, false if too long.
 */
export function validateNoteLength(note: string): boolean {
	// Allow empty, null, or non-string notes (note is optional)
	if (StringUtils.isNullOrWhitespace(note)) {
		return true;
	}

	const trimmedNote = Util.trimString(note);

	// If trimming results in an empty string, it's still valid
	if (!trimmedNote) {
		return true;
	}

	// Validate the trimmed note's length
	return trimmedNote.length <= MAX_NOTE_LENGTH;
}

//#endregion
